import {
  DevolutivaFormData,
  DevolutivaSchema,
} from "@/components/access-request/validate-form";
import { ACCESS_REQUEST_BASE_PATH } from "@/constants/access-request";
import { DEVOLUTIVA_MESSAGES } from "@/constants/messages";
import { StatusEnumAccessRequest } from "@/enum/access_request";
import { useUpdatedRequestMutation } from "@/services/request-access";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Load } from "components/load";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Textarea } from "components/ui/textarea";
import { toast } from "components/ui/use-toast";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

interface DevolutivaFormProps {
  redirectPath?: string;
}

export const DevolutivaForm = ({
  redirectPath = ACCESS_REQUEST_BASE_PATH.FINANCE,
}: DevolutivaFormProps): JSX.Element => {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<DevolutivaFormData>({
    resolver: zodResolver(DevolutivaSchema),
    mode: "onChange",
    defaultValues: { comment: "" },
  });
  const { id_request } = useParams();
  const navigate = useNavigate();
  const [updatedRequest, { isLoading }] = useUpdatedRequestMutation();

  const onSubmit = async (data: DevolutivaFormData): Promise<void> => {
    try {
      await updatedRequest({
        comment: data.comment,
        id: id_request?.toString() || "",
        status: StatusEnumAccessRequest.REJECTED,
      }).unwrap();
      toast({
        variant: "success",
        description: DEVOLUTIVA_MESSAGES.TOAST_SUCCESS,
      });
      navigate(redirectPath);
    } catch (error) {
      toast({
        variant: "error",
        description: extractRtkErrorMessage(
          error,
          DEVOLUTIVA_MESSAGES.TOAST_ERROR,
        ),
      });
    }
  };

  return (
    <>
      {isLoading && (
        <div className="w-full h-screen bg-white opacity-60 fixed top-0 left-0 z-50 pointer-events-auto">
          <Load />
        </div>
      )}
      <Card className="w-full w-full">
        <CardHeader>
          <CardTitle>{DEVOLUTIVA_MESSAGES.TITLE}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="comentario" className="text-sm font-medium">
                {DEVOLUTIVA_MESSAGES.LABEL_COMMENT}
              </label>
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="comentario"
                    placeholder={DEVOLUTIVA_MESSAGES.PLACEHOLDER}
                    aria-invalid={errors.comment ? "true" : "false"}
                  />
                )}
              />
              {errors.comment && (
                <p className="text-sm text-red-600">{errors.comment.message}</p>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={!isValid || isLoading}
                title={!isValid ? DEVOLUTIVA_MESSAGES.TOOLTIP_DISABLED : undefined}
              >
                {DEVOLUTIVA_MESSAGES.BUTTON_REJECT}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
};
